import styles from './discussion-comment.module.css'

import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuChevronUp } from '@react-icons/all-files/lu/LuChevronUp'
import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import { LuEye } from '@react-icons/all-files/lu/LuEye'
import clsx from 'clsx'
import dayjs from 'dayjs'
import * as React from 'react'

import { useDeleteDiscussionMutation } from '@disco/state-management/api/discussions'
import { pushUINotification } from '@disco/utils/notifications'

import type { Discussion } from 'types/discussion'

import {
  Badge,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'
import UserAvatar from 'components/ui/user-avatar'

import { isEditorOpen, setEditorOpen } from './comment-draft-cache'
import CommentEditor from './comment-editor'
import DiscussionBody from './discussion-body'
import LikeButton from './like-button'
import { EXTERNAL_TOOLTIP_TEXT } from './visibility-control'

type Props = {
  discussion: Discussion
  isReply?: boolean
  trackDurationSeconds?: number
  onTimestampClick?: (timestamp: string) => void
}

const formatCommentDate = (timestamp: string): string =>
  dayjs(timestamp).format('MMM D, YYYY, h:mma')

const formatUserName = (user: { first_name: string; last_name: string }) =>
  `${user.first_name} ${user.last_name}`.trim()

const DiscussionComment = ({
  discussion,
  isReply = false,
  trackDurationSeconds,
  onTimestampClick,
}: Props): React.ReactElement => {
  const [showReplies, setShowReplies] = React.useState(true)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(() =>
    isEditorOpen('edit', discussion.id),
  )
  const [showReplyEditor, setShowReplyEditor] = React.useState(() =>
    isEditorOpen('reply', discussion.id),
  )
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false)

  React.useEffect(() => {
    setEditorOpen('edit', discussion.id, isEditing)
  }, [discussion.id, isEditing])

  React.useEffect(() => {
    setEditorOpen('reply', discussion.id, showReplyEditor)
  }, [discussion.id, showReplyEditor])

  const [deleteDiscussion, { isLoading: isDeleting }] =
    useDeleteDiscussionMutation()

  const author = discussion.created_by
  const authorName = formatUserName(author)
  const isClient = discussion.visibility === 'client'
  const wasEdited = discussion.modified_by !== null

  const replies = !isReply ? discussion.replies ?? [] : []
  const repliesCount = replies.length
  const repliesOpen = !isClient && repliesCount > 0 && showReplies

  const handleEditClick = () => {
    setMenuOpen(false)
    setIsEditing(true)
  }

  const handleDeleteClick = () => {
    setMenuOpen(false)
    setShowDeleteDialog(true)
  }

  const handleReplyClick = () => {
    setShowReplies(true)
    setShowReplyEditor(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteDiscussion({
        id: discussion.id,
        parent: discussion.parent,
        target_type: discussion.target_type,
        target_object_id: discussion.target_object_id,
      }).unwrap()
      setShowDeleteDialog(false)
    } catch {
      pushUINotification({
        message: 'Failed to delete note',
        level: 'error',
      })
    }
  }

  return (
    <div
      data-testid={`discussion-comment-${discussion.id}`}
      className={clsx(
        styles.row,
        isReply && styles.replyRow,
        (repliesOpen || showReplyEditor) && styles.rowWithThread,
      )}
    >
      <div className={styles.commentBody}>
        <Flex gap="3" align="start">
          <UserAvatar
            name={authorName}
            size="32px"
            fontSize="13px"
            className={styles.avatar}
          />
          <Flex
            direction="column"
            gap="1"
            flexGrow="1"
            minWidth="0"
            minHeight="0"
          >
            <Flex align="center" gap="2" wrap="wrap">
              <Text size="2" weight="bold">
                {authorName}
              </Text>
              {isClient && (
                <Tooltip content={EXTERNAL_TOOLTIP_TEXT}>
                  <Badge color="amber" variant="soft" radius="medium" size="1">
                    <LuEye size={12} aria-hidden />
                    Client
                  </Badge>
                </Tooltip>
              )}
            </Flex>
            <Text size="1" color="gray">
              {wasEdited && discussion.modified_by
                ? `Last edited by ${formatUserName(
                    discussion.modified_by,
                  )}, ${formatCommentDate(discussion.modified)}`
                : formatCommentDate(discussion.created)}
            </Text>

            <div className={styles.body}>
              {isEditing ? (
                <CommentEditor
                  mode="edit"
                  trackId={discussion.target_object_id as number}
                  discussion={discussion}
                  trackDurationSeconds={trackDurationSeconds}
                  onCancel={() => setIsEditing(false)}
                  onSuccess={() => setIsEditing(false)}
                />
              ) : (
                <>
                  <DiscussionBody
                    key={discussion.modified}
                    body={discussion.body}
                    trackDurationSeconds={trackDurationSeconds}
                    onTimestampClick={onTimestampClick}
                  />
                  {wasEdited && (
                    <Text size="1" color="gray" className={styles.editedMark}>
                      (Edited)
                    </Text>
                  )}
                </>
              )}
            </div>

            {!isEditing && !isClient && (
              <Flex align="center" gap="1" className={styles.actions}>
                <LikeButton discussion={discussion} />
                {!isReply && !showReplyEditor && (
                  <Button
                    size="1"
                    variant="ghost"
                    color="gray"
                    onClick={handleReplyClick}
                    aria-label="Reply"
                    ml="1"
                    className={styles.focus}
                  >
                    Reply
                  </Button>
                )}
              </Flex>
            )}
          </Flex>

          {!isEditing && (
            <div className={clsx(styles.menu, menuOpen && styles.menuOpen)}>
              <DropdownMenu.Root
                modal={false}
                open={menuOpen}
                onOpenChange={setMenuOpen}
              >
                <DropdownMenu.Trigger>
                  <IconButton
                    size="1"
                    variant="ghost"
                    color="gray"
                    aria-label="Comment actions"
                  >
                    <LuEllipsis size={16} />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  variant="soft"
                  align="end"
                  onKeyDownCapture={e => {
                    if (e.key === 'Tab') {
                      e.stopPropagation()
                      setMenuOpen(false)
                    }
                  }}
                >
                  <DropdownMenu.Item onSelect={handleEditClick}>
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item color="red" onSelect={handleDeleteClick}>
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          )}
        </Flex>
      </div>

      {!isClient && !isReply && (repliesCount > 0 || showReplyEditor) && (
        <div
          className={clsx(
            styles.repliesWrapper,
            (showReplies || showReplyEditor) && styles.repliesWrapperOpen,
          )}
        >
          {(showReplies || showReplyEditor) && (
            <div className={styles.replies}>
              {showReplies &&
                replies.map(reply => (
                  <DiscussionComment
                    key={reply.id}
                    discussion={reply}
                    isReply
                    trackDurationSeconds={trackDurationSeconds}
                    onTimestampClick={onTimestampClick}
                  />
                ))}
              {showReplyEditor && (
                <div className={styles.replyEditor}>
                  <CommentEditor
                    mode="reply"
                    trackId={discussion.target_object_id as number}
                    parent={discussion}
                    trackDurationSeconds={trackDurationSeconds}
                    onCancel={() => setShowReplyEditor(false)}
                    onSuccess={() => setShowReplyEditor(false)}
                  />
                </div>
              )}
            </div>
          )}
          {repliesCount > 0 && (
            <button
              type="button"
              className={styles.repliesToggle}
              onClick={() => setShowReplies(s => !s)}
              aria-expanded={showReplies}
            >
              {showReplies ? (
                <LuChevronUp size={14} aria-hidden />
              ) : (
                <LuChevronDown size={14} aria-hidden />
              )}
              <span>{`${showReplies ? 'Hide' : 'Show'} replies`}</span>
            </button>
          )}
        </div>
      )}

      <Dialog.Root open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>Delete this note?</Dialog.Title>
          <Dialog.Description>
            <span>
              {discussion.replies_count > 0 &&
                'Delete this note and all replies? '}
            </span>
            This action cannot be undone.
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                size="2"
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              variant="solid"
              color="red"
              size="2"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              loading={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  )
}

export default DiscussionComment
